import React from "react";
import { useSelector } from "react-redux";
import { Badge, Box, Typography } from "@mui/material";
import { AddShoppingCart, Discount, Edit, IosShare, Download, DisplaySettings, Check, CheckCircle } from "@mui/icons-material";
// import { StyledTableCell, StyledStack, IconContainer, HtmlTooltip, HtmlTooltipDisabled } from './StyledComponents';
import { ListingCellCenter, ListingCellLeft, StyledTableCell } from "../../../components/commonComponents/TabularLists/styles";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  IconContainer,
  StyledDarkRedInformationTypography,
  StyledStack,
  ThumbnailText,
  ThumbnailWithText,
} from "../../../components/commonComponents/StyledComponents/styled";
import { checkUserPermissions } from "../../../api_calls/userManagement";
import { fetchRequiredDetailsBeforePopup } from "../../../api_calls/marketingActivities";
import { fetchReportSettings } from "../../../api_calls/ReportSettings";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";
import { fontSize } from "@mui/system";

const ActionIcon = ({ condition, availableTitle, notAvailableTitle, icon, onClick, ariaLabelText }) =>
  condition ? (
    <HtmlTooltip title={availableTitle}>
      <a href="javascript:void(0)" onClick={onClick} aria-label={ariaLabelText} style={{ display: "inline-flex", alignItems: "center", paddingBottom: "8px" }}>
        {React.cloneElement(icon, { sx: { color: "#AF3241" } })}
      </a>
    </HtmlTooltip>
  ) : availableTitle !== "Report Settings" ? (
    <HtmlTooltipDisabled title={notAvailableTitle}>
      <a href="javascript:void(0)" style={{ display: "inline-flex", alignItems: "center", paddingBottom: "8px" }}>
        {React.cloneElement(icon, { sx: { color: "#E6E6E6" } })}
      </a>
    </HtmlTooltipDisabled>
  ) : (
    ""
  );

const CampaignActions = ({
  campaign,
  checkEditAccess,
  campaignDetails,
  userInfo,
  userRole,
  checkRequestAccess,
  handlePopoverOpen,
  setCampaignInfo,
  setOpenModal,
  fetchCampaign,
  setDownloadOptions,
  downloadDataOptions,
  reportSettingsOptions,
  setReportSettingsOptions,
}) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  const isAvailableToList =
    userInfo.current_workspace.name === campaign.retailer_company_name || userInfo.user_type === "cyreen";
  const isEditAccessible = checkEditAccess.success === true && isAvailableToList;
  const isNotScheduledOrCompleted = ![2, 4].includes(campaign.campaign_status_code);
  const isUserAuthorized =
    isEditAccessible &&
    typeof userInfo.current_workspace !== "undefined" &&
    (userInfo.current_workspace.id === campaign.retailer_company_id || userInfo.user_type === "cyreen");
  const isReportShareable =
    isEditAccessible && campaign.campaign_status !== "Scheduled" && campaign.campaign_status_code === 4;
  const isDataDownloadable =
    checkRequestAccess.success === true &&
    isAvailableToList &&
    (campaign.campaign_status !== "Scheduled" || campaign.impression_report_status !== 0);
  const isAdminUser = userInfo.user_type === "cyreen";

  return (
    <ListingCellCenter>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2, marginTop: "9px" }}>
        {/* ------- manage products (if allowed) -------- */}
        {
          permissionScope.campaign_products_can_edit === true && (
            <ActionIcon
              condition={isEditAccessible}
              availableTitle="Manage analytics products"
              notAvailableTitle="Not Available"
              ariaLabelText={"Manage analytics products for " + campaign.campaign_name}
              icon={<AddShoppingCart />}
              onClick={(e) => {
                e.preventDefault();
                setOpenModal((prevState) => ({ ...prevState, products: true }));
                // const productActionItems = isNotScheduledOrCompleted ? ["Add new product", "List products"] : ["List products"];
                // handlePopoverOpen(e, productActionItems);
                setCampaignInfo(campaign);
              }}
            />)
        }

        {/* ------- manage activities (if allowed) -------- */}
        {
          permissionScope.campaign_activities_can_edit === true && (
            <ActionIcon
              condition={isEditAccessible && ![0, 1].includes(campaign.campaign_status_code)}
              availableTitle="Manage marketing activities"
              notAvailableTitle="Please add analytics products before adding activities"
              ariaLabelText={"Manage marketing activities for " + campaign.campaign_name}
              icon={<Discount />}
              onClick={(e) => {
                e.preventDefault();
                // const activityActionItems = isNotScheduledOrCompleted
                //   ? ["Add new activity", "List activities"]
                //   : ["List activities"];
                setCampaignInfo(campaign);
                fetchRequiredDetailsBeforePopup(campaign).then(() => {
                  setOpenModal((prevState) => ({ ...prevState, activity_list: true }));
                });


              }}
            />
          )
        }

        {/* ------- edit campaign (if allowed) ------- */}
        {
          permissionScope.campaign_list_can_edit === true && (
            <ActionIcon
              condition={isUserAuthorized}
              availableTitle={<Typography variant="body4">Edit</Typography>}
              notAvailableTitle="Not Available"
              ariaLabelText={"edit campaign for " + campaign.campaign_name}
              icon={<Edit />}
              onClick={(e) => {
                e.preventDefault();
                setOpenModal((prevState) => ({ ...prevState, edit_campaign: true }));
                setCampaignInfo(campaign);
              }}
            />
          )
        }

        {/* --------- share campaign (if allowed) -------- */}
        {
          permissionScope.campaign_can_share === true && (
            <ActionIcon
              condition={isReportShareable}
              availableTitle="Share report"
              notAvailableTitle="Not Available"
              icon={
                <Badge
                  sx={{ display: "flex" }}
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    campaign.shared === true && isReportShareable === true ? (
                      <CheckCircle
                        sx={{
                          fontSize: "13px !important",
                          color: "#fff",
                          backgroundColor: "#AF3241",
                          borderRadius: "50%",
                        }}
                      />
                    ) : null
                  }
                >
                  <IosShare />
                </Badge>
              }
              onClick={(e) => {
                e.preventDefault();
                fetchCampaign(campaign.campaign_id, "all").then(() => {
                  setOpenModal((prevState) => ({ ...prevState, share_report: true }));
                  setCampaignInfo(campaign);
                });
              }}
            />
          )
        }

        {/* ----------- download data (if allowed) --------- */}
        {
          permissionScope.campaign_can_download === true && (
            <ActionIcon
              condition={isDataDownloadable}
              availableTitle="Download Campaign Data"
              notAvailableTitle="Not Available"
              icon={<Download />}
              onClick={(e) => {
                e.preventDefault();
                setDownloadOptions(true);
                handlePopoverOpen(e, Object.keys(downloadDataOptions));
                setCampaignInfo(campaign);
              }}
            />
          )
        }

        {/* ----------- Report settings (if allowed) -------- */}
        {
          permissionScope.campaign_reports_internal_settings_can_view === true && (
            <ActionIcon
              condition={isAdminUser}
              availableTitle="Internal Settings"
              notAvailableTitle="Not Available"
              ariaLabelText={"report settings for " + campaign.campaign_name}
              icon={<DisplaySettings />}
              onClick={(e) => {
                e.preventDefault();
                setReportSettingsOptions(true);
                handlePopoverOpen(e, reportSettingsOptions);
                setCampaignInfo(campaign)

                // setBatchLoadingStatus(true);
                // fetchReportSettings(campaign.campaign_id).then(() => {
                //   setOpenModal((prevState) => ({ ...prevState, report_settings: true }));
                //   setCampaignInfo(campaign);
                // }).finally(() => {
                //   setBatchLoadingStatus(false);
                // });
              }}
            />
          )
        }

      </Box>
    </ListingCellCenter>
  );
};

export default CampaignActions;
